import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLiveEvents } from "@/lib/liveEvents";
import { toast } from "sonner";
import { useGeolocation } from "@/hooks/useGeolocation";
import { playPing } from "@/lib/ping";
import { AISearchBar } from "@/components/radar/AISearchBar";
import { GeoStatusPill } from "@/components/radar/GeoStatusPill";
import { GeoPermissionBanner } from "@/components/radar/GeoPermissionBanner";
import { RadarSurface } from "@/components/radar/RadarSurface";
import { NearbyList } from "@/components/radar/NearbyList";
import { SearchResults } from "@/components/radar/SearchResults";
import { UserSheet } from "@/components/radar/UserSheet";
import { HangoutBar } from "@/components/radar/HangoutBar";

export default function Radar() {
  const { user, refresh } = useAuth();
  const { on } = useLiveEvents();
  const navigate = useNavigate();
  const [nearby, setNearby] = useState([]);
  const [selected, setSelected] = useState(null);
  const [matchInfo, setMatchInfo] = useState(null);
  const [matching, setMatching] = useState(false);
  const [cachedScores, setCachedScores] = useState({}); // { userId: {score, reason, icebreaker} }
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [sharing, setSharing] = useState(user?.location_sharing ?? true);
  const [hangout, setHangout] = useState(null);
  const [hangoutMembers, setHangoutMembers] = useState([]);
  const prevIdsRef = useRef(new Set());

  const loadNearby = async () => {
    try {
      const { data } = await api.get("/nearby", { params: { radius_km: 25 } });
      const newIds = new Set(data.users.map((u) => u.id));
      if (prevIdsRef.current.size > 0) {
        let anyNew = false;
        for (const u of data.users) {
          if (!prevIdsRef.current.has(u.id)) {
            anyNew = true;
            toast.success(`${u.display_name} entered your signal · ${u.distance_km}km`, {
              icon: <Zap size={14} className="text-[#39FF14]" />,
            });
          }
        }
        if (anyNew) playPing();
      }
      prevIdsRef.current = newIds;
      setNearby(data.users);
      // Bulk lookup cached AI scores for these nearby users (no LLM call).
      if (data.users.length > 0) {
        try {
          const ids = data.users.map((u) => u.id).join(",");
          const { data: bulk } = await api.get("/ai/matches/bulk", { params: { ids } });
          setCachedScores(bulk.matches || {});
        } catch { /* silent */ }
      } else {
        setCachedScores({});
      }
    } catch { /* silent */ }
  };

  const { geoState, geoError, coords, enable } = useGeolocation({
    onFix: async () => {
      await loadNearby();
      await refresh();
    },
  });

  useEffect(() => {
    loadNearby();
    const int = setInterval(loadNearby, 15000);
    return () => clearInterval(int);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHangout = async () => {
    try {
      const { data } = await api.get("/hangouts/active");
      setHangout(data.hangout || null);
      setHangoutMembers(data.members || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadHangout();
    const unsub1 = on("hangout_change", () => loadHangout());
    const unsub2 = on("hangout_positions", (p) => {
      setHangoutMembers((prev) => {
        const next = prev.slice();
        const idx = next.findIndex((m) => m.id === p.user_id);
        const patch = { id: p.user_id, display_name: p.display_name, lat: p.lat, lng: p.lng, last_seen: p.at };
        if (idx >= 0) next[idx] = { ...next[idx], ...patch };
        else next.push(patch);
        return next;
      });
    });
    return () => { unsub1(); unsub2(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leaveHangout = async () => {
    if (!hangout) return;
    try { await api.post(`/hangouts/${hangout.id}/leave`); } catch { /* ignore */ }
    setHangout(null);
    setHangoutMembers([]);
    toast("Left hangout");
  };

  const endHangout = async () => {
    if (!hangout) return;
    try {
      await api.post(`/hangouts/${hangout.id}/end`);
      setHangout(null);
      setHangoutMembers([]);
      toast.success("Hangout ended");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not end hangout");
    }
  };

  const toggleShare = async (val) => {
    setSharing(val);
    await api.patch("/profile", { location_sharing: val });
    await refresh();
    toast(val ? "Broadcasting" : "Signal hidden");
  };

  const openUser = async (u) => {
    setSelected(u);
    const cached = cachedScores[u.id];
    if (cached) {
      // Instant render from cache — no spinner.
      setMatchInfo(cached);
      setMatching(false);
    } else {
      setMatchInfo(null);
      setMatching(true);
    }
    try {
      const { data } = await api.post("/ai/match", { other_user_id: u.id });
      setMatchInfo(data);
      // Update our cache map so future opens are instant.
      setCachedScores((prev) => ({ ...prev, [u.id]: { ...data, cached: true } }));
    } catch {
      if (!cached) setMatchInfo({ score: 0, reason: "AI unavailable", icebreaker: "" });
    } finally {
      setMatching(false);
    }
  };

  const doSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setSearchResults(null);
    try {
      const { data } = await api.post("/ai/search", { query });
      setSearchResults(data);
    } catch {
      toast.error("AI search failed");
    } finally {
      setSearching(false);
    }
  };

  const sendSignal = async () => {
    try {
      await api.post("/signals/send", {
        recipient_id: selected.id,
        opener: matchInfo?.icebreaker || "",
      });
      toast.success(`Signal sent to ${selected.display_name}`);
      setSelected(null);
      navigate("/threads");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send signal");
    }
  };

  return (
    <div className="relative">
      <AISearchBar
        query={query}
        setQuery={setQuery}
        onSubmit={doSearch}
        searching={searching}
        sharing={sharing}
        onToggleShare={toggleShare}
      />

      <div className="px-4 md:px-8 pb-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-[10px] tracking-[0.3em] font-mono text-white/40">
            // {nearby.length} SIGNAL{nearby.length !== 1 && "S"} IN 25KM
          </div>
          <GeoStatusPill geoState={geoState} coords={coords} />
        </div>

        <GeoPermissionBanner geoState={geoState} geoError={geoError} onEnable={enable} />

        <HangoutBar
          hangout={hangout}
          members={hangoutMembers}
          selfId={user?.id}
          onLeave={leaveHangout}
          onEnd={endHangout}
        />

        <RadarSurface
          nearby={nearby}
          coords={coords}
          onDotClick={openUser}
          hangoutMembers={hangoutMembers}
          selfId={user?.id}
        />
      </div>

      <SearchResults results={searchResults} onClose={() => setSearchResults(null)} onOpenUser={openUser} />

      <NearbyList nearby={nearby} onOpenUser={openUser} cachedScores={cachedScores} />

      <UserSheet
        user={selected}
        matchInfo={matchInfo}
        matching={matching}
        onClose={() => setSelected(null)}
        onSend={sendSignal}
      />
    </div>
  );
}
